import { useQuery } from '@tanstack/react-query';
import {
  type CreateHotelInventoryRequest,
  type HotelInventoryMealOptionInput,
  type HotelInventoryListItem,
} from '@utazzo/shared';
import {
  UzButton,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { Plus, Trash2, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { CurrencyPicker, HotelRoomPicker, MealPlanPicker } from '@/components/pickers';
import * as currenciesApi from '@/features/currencies/currencies.api';

/**
 * Price period form: a date range + ALL price components for that range -
 * per-pax rates, extra beds, and meal supplements (B5.16: meals + extra beds
 * moved off the room onto the period so they can vary by season). The room
 * carries only identity + occupancy.
 *
 * Quick-range presets cover the common entry patterns: single day, whole
 * month, multiple months, full year, open-ended.
 */

interface RoomScope {
  id: string;
  name: string;
}

interface RateCardFormPanelProps {
  hotelId: string;
  /** Lock the period to this room and hide the room picker. */
  roomScope?: RoomScope;
  initial: HotelInventoryListItem | null;
  pending: boolean;
  onSubmit: (payload: CreateHotelInventoryRequest) => Promise<void>;
  onCancel: () => void;
}

interface MealOptionDraft {
  mealPlanId: string | null;
  /** Plain amounts in the period's currency (up to 2 decimals). */
  perAdult: number;
  perChild: number;
}

interface FormValues {
  hotelRoomId: string | null;
  /** Base occupancy (adults) this rate covers; null = fall back to 2. */
  baseOccupancy: number | null;
  currencyId: string | null;
  validityFrom: string;
  validityTo: string;
  /** Plain price amounts (up to 2 decimals) in the picked currency. */
  adultPrice: number;
  childWithBedPrice: number;
  infantPrice: number;
  extraAdultPrice: number;
  extraChildPrice: number;
  extraBedChildPrice: number;
  fixedRoomCost: number;
  weekdayMask: number;
  /** Sold-out / blocked dates within the window (YYYY-MM-DD). */
  blackoutDates: string[];
  mealOptions: MealOptionDraft[];
  isActive: boolean;
}

const iso = (d: Date): string => d.toISOString().slice(0, 10);

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

// Day-of-week mask: bit0=Sun..bit6=Sat.
const MASK_ALL = 127;
const MASK_WEEKDAYS = 0b0111110; // Mon-Fri (bits 1-5)
const MASK_WEEKENDS = 0b1000001; // Sat (bit6) + Sun (bit0)
const DOW = [
  { bit: 1, label: 'Mon' },
  { bit: 2, label: 'Tue' },
  { bit: 3, label: 'Wed' },
  { bit: 4, label: 'Thu' },
  { bit: 5, label: 'Fri' },
  { bit: 6, label: 'Sat' },
  { bit: 0, label: 'Sun' },
] as const;

/** Quick-range presets for the validity window. */
const buildPresets = (): ReadonlyArray<{ label: string; from: string; to: string }> => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return [
    { label: 'Today only', from: iso(now), to: iso(now) },
    { label: 'This month', from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    { label: 'Next 3 months', from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 3, 0)) },
    { label: 'Next 6 months', from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 6, 0)) },
  ];
};

/** Default validity range for a new period: the "Next 3 months" preset. */
const next3MonthsRange = (): { from: string; to: string } => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 3, 0)) };
};

/** Money input: a plain amount with up to 2 decimals, in the picked currency. */
const MoneyField = ({
  name,
  label,
  hint,
  pending,
  symbol,
  required,
}: {
  name: keyof FormValues & string;
  label: string;
  hint?: string;
  pending: boolean;
  symbol: string;
  required?: boolean;
}): React.JSX.Element => (
  <UzFormField name={name}>
    {({ field, error }) => (
      <UzNumberInput
        name={field.name}
        label={label}
        required={required}
        precision={2}
        min={0}
        leftAdornment={<span className="text-sm text-zinc-500">{symbol}</span>}
        value={(field.value as number | undefined) ?? ''}
        onValueChange={(v) => field.onChange(v ?? 0)}
        hint={hint}
        error={error}
        disabled={pending}
      />
    )}
  </UzFormField>
);

/** Stacked field group: small heading + fields. */
const Group = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className="space-y-3">
    <h5 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">{title}</h5>
    {children}
  </div>
);

/**
 * Live example computed from the prices being typed - the same formula the
 * itinerary picker uses to assemble a night from the traveller mix.
 */
const PricePreview = ({
  values,
  symbol,
}: {
  values: FormValues;
  symbol: string;
}): React.JSX.Element | null => {
  const n = (v: number): number => (Number.isFinite(v) ? v : 0);
  const fmt = (amount: number): string =>
    `${symbol}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const fixed = n(values.fixedRoomCost);
  const adult = n(values.adultPrice);
  const child = n(values.childWithBedPrice);
  const extraChild = n(values.extraChildPrice);

  if (fixed > 0) {
    return (
      <div className="rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-xs text-zinc-600">
        Fixed room price: <span className="font-semibold tabular-nums">{fmt(fixed)}</span> / night -
        charged per room, ignoring the per-person fields.
      </div>
    );
  }
  if (adult === 0) return null;

  const parts: string[] = [`2 adults ${fmt(2 * adult)}`];
  let total = 2 * adult;
  if (child > 0) {
    parts.push(`1 child ${fmt(child)}`);
    total += child;
  }
  if (extraChild > 0) {
    parts.push(`1 extra child ${fmt(extraChild)}`);
    total += extraChild;
  }
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-xs text-zinc-600">
      Example night: {parts.join(' + ')} ={' '}
      <span className="font-semibold tabular-nums">{fmt(total)}</span>. Meals and extra beds are
      added per person on top.
    </div>
  );
};

export const RateCardFormPanel = ({
  hotelId,
  roomScope,
  initial,
  pending,
  onSubmit,
  onCancel,
}: RateCardFormPanelProps): React.JSX.Element => {
  const form = useForm<FormValues>({
    defaultValues: initial
      ? {
          hotelRoomId: initial.hotelRoomId,
          baseOccupancy: initial.baseOccupancy ?? null,
          currencyId: initial.currencyId,
          validityFrom: initial.validityFrom,
          validityTo: initial.validityTo,
          adultPrice: initial.adultPrice,
          childWithBedPrice: initial.childWithBedPrice,
          infantPrice: initial.infantPrice,
          extraAdultPrice: initial.extraAdultPrice,
          extraChildPrice: initial.extraChildPrice,
          extraBedChildPrice: initial.extraBedChildPrice,
          fixedRoomCost: initial.fixedRoomCost,
          weekdayMask: initial.weekdayMask,
          blackoutDates: initial.blackoutDates ?? [],
          mealOptions: initial.mealOptions.map((m) => ({
            mealPlanId: m.mealPlanId,
            perAdult: m.perAdult,
            perChild: m.perChild,
          })),
          isActive: initial.isActive,
        }
      : {
          hotelRoomId: roomScope?.id ?? null,
          baseOccupancy: null,
          currencyId: null,
          validityFrom: next3MonthsRange().from,
          validityTo: next3MonthsRange().to,
          adultPrice: 0,
          childWithBedPrice: 0,
          infantPrice: 0,
          extraAdultPrice: 0,
          extraChildPrice: 0,
          extraBedChildPrice: 0,
          fixedRoomCost: 0,
          weekdayMask: MASK_ALL,
          blackoutDates: [],
          mealOptions: [],
          isActive: true,
        },
  });

  // Price adornment follows the picked currency.
  const currencyId = form.watch('currencyId');
  const currencyQuery = useQuery({
    queryKey: ['currencies', 'detail', currencyId ?? ''],
    queryFn: () => currenciesApi.getCurrency(currencyId as string),
    enabled: Boolean(currencyId),
    staleTime: 5 * 60_000,
  });
  const symbol = currencyId
    ? (currencyQuery.data?.symbol ?? currencyQuery.data?.code ?? '...')
    : '₹';

  // Default a new period's currency to INR (the common case) so operators
  // don't have to pick it every time. Editing keeps whatever it already had.
  const currencyListQuery = useQuery({
    queryKey: ['currencies', 'list', 'rate-default'],
    queryFn: () => currenciesApi.listCurrencies({ page: 1, pageSize: 200, sortDir: 'asc' }),
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (initial || form.getValues('currencyId')) return;
    const inr = currencyListQuery.data?.data.find((c) => c.code === 'INR');
    if (inr) form.setValue('currencyId', inr.id);
  }, [currencyListQuery.data, initial, form]);
  const watched = form.watch();
  const mealOptions = form.watch('mealOptions');
  const setMealOptions = (next: MealOptionDraft[]): void => {
    form.setValue('mealOptions', next, { shouldDirty: true });
  };

  const blackoutDates = form.watch('blackoutDates');
  const addBlackout = (d: string): void => {
    if (!d) return;
    // Only dates inside the validity window, no duplicates.
    if (d < watched.validityFrom || d > watched.validityTo) return;
    if (blackoutDates.includes(d)) return;
    form.setValue('blackoutDates', [...blackoutDates, d].sort(), { shouldDirty: true });
  };
  const removeBlackout = (d: string): void =>
    form.setValue(
      'blackoutDates',
      blackoutDates.filter((x) => x !== d),
      { shouldDirty: true },
    );

  const submit = form.handleSubmit(async (values) => {
    // Validation: a period must price per-pax OR be a fixed-room buyout.
    // When not a fixed-cost buyout, per-adult and per-child are mandatory.
    const isFixed = values.fixedRoomCost > 0;
    let invalid = false;
    if (!isFixed) {
      if (!values.adultPrice || values.adultPrice <= 0) {
        form.setError('adultPrice', { type: 'required', message: 'Per-adult price is required' });
        invalid = true;
      }
      if (!values.childWithBedPrice || values.childWithBedPrice <= 0) {
        form.setError('childWithBedPrice', {
          type: 'required',
          message: 'Per-child price is required',
        });
        invalid = true;
      }
    }
    if (invalid) return;

    await onSubmit({
      hotelRoomId: roomScope ? roomScope.id : (values.hotelRoomId ?? null),
      baseOccupancy: values.baseOccupancy ?? null,
      currencyId: values.currencyId ?? null,
      validityFrom: values.validityFrom,
      validityTo: values.validityTo,
      adultPrice: values.adultPrice,
      childWithBedPrice: values.childWithBedPrice,
      infantPrice: values.infantPrice,
      extraAdultPrice: values.extraAdultPrice,
      extraChildPrice: values.extraChildPrice,
      extraBedChildPrice: values.extraBedChildPrice,
      fixedRoomCost: values.fixedRoomCost,
      weekdayMask: values.weekdayMask,
      blackoutDates: values.blackoutDates,
      mealOptions: values.mealOptions
        .filter((m): m is MealOptionDraft & { mealPlanId: string } => Boolean(m.mealPlanId))
        .map(
          (m): HotelInventoryMealOptionInput => ({
            mealPlanId: m.mealPlanId,
            perAdult: m.perAdult,
            perChild: m.perChild,
          }),
        ),
      isActive: values.isActive,
    });
  });

  const presets = buildPresets();

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-300 bg-white shadow-sm">
      <header className="border-b border-zinc-200 bg-zinc-50/60 px-5 py-3">
        <h4 className="text-sm font-semibold tracking-tight text-zinc-900">
          {initial
            ? 'Edit price period'
            : roomScope
              ? `New price period for ${roomScope.name}`
              : 'New price period'}
        </h4>
        <p className="mt-0.5 text-xs text-zinc-500">
          All price components for a date range - per-pax rates, extra beds and meal supplements.
        </p>
      </header>

      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-6 px-5 py-5">
          <Group title="Dates">
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => {
                const active = watched.validityFrom === p.from && watched.validityTo === p.to;
                return (
                  <button
                    key={p.label}
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      form.setValue('validityFrom', p.from, { shouldDirty: true });
                      form.setValue('validityTo', p.to, { shouldDirty: true });
                    }}
                    className={
                      'rounded-full border px-2.5 py-1 text-xs transition-colors ' +
                      (active
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900')
                    }
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <UzFormField name="validityFrom">
                {({ field, error }) => (
                  <UzDateTimePicker
                    mode="date"
                    displayFormat="dd/MM/yyyy"
                    placeholder="dd/mm/yyyy"
                    label="From"
                    required
                    value={ymdToDate(field.value as string | undefined)}
                    onValueChange={(d) => field.onChange(dateToYmd(d))}
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
              <UzFormField name="validityTo">
                {({ field, error }) => (
                  <UzDateTimePicker
                    mode="date"
                    displayFormat="dd/MM/yyyy"
                    placeholder="dd/mm/yyyy"
                    label="To"
                    required
                    value={ymdToDate(field.value as string | undefined)}
                    onValueChange={(d) => field.onChange(dateToYmd(d))}
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
            </div>
            {/* Applies-on day-of-week selector. Lets weekday vs weekend
                pricing in the same month be two periods over the same range. */}
            <UzFormField name="weekdayMask">
              {({ field }) => {
                const mask = (field.value as number) ?? MASK_ALL;
                const set = (m: number): void => field.onChange(m);
                const toggle = (bit: number): void => set(mask ^ (1 << bit));
                const presetBtn = (label: string, m: number): React.JSX.Element => (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => set(m)}
                    className={
                      'rounded-full border px-2.5 py-1 text-xs transition-colors ' +
                      (mask === m
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                    }
                  >
                    {label}
                  </button>
                );
                return (
                  <div className="space-y-2">
                    <span className="text-xs font-medium text-zinc-700">Applies on</span>
                    <div className="flex flex-wrap gap-1.5">
                      {presetBtn('All days', MASK_ALL)}
                      {presetBtn('Weekdays', MASK_WEEKDAYS)}
                      {presetBtn('Weekends', MASK_WEEKENDS)}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {DOW.map((d) => (
                        <button
                          key={d.bit}
                          type="button"
                          disabled={pending}
                          onClick={() => toggle(d.bit)}
                          className={
                            'h-7 w-9 rounded border text-xs transition-colors ' +
                            ((mask & (1 << d.bit)) !== 0
                              ? 'border-zinc-300 bg-zinc-100 font-medium text-zinc-900'
                              : 'border-zinc-200 text-zinc-400 hover:bg-zinc-50')
                          }
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }}
            </UzFormField>
            {!roomScope && (
              <UzFormField name="hotelRoomId">
                {({ field, error }) => (
                  <HotelRoomPicker
                    hotelId={hotelId}
                    value={(field.value as string | null | undefined) ?? null}
                    onValueChange={(v) => field.onChange(v)}
                    hint="Attach this period to a room"
                    disabled={pending}
                    error={error}
                  />
                )}
              </UzFormField>
            )}
            <div className="md:w-1/2 md:pr-2">
              <UzFormField name="baseOccupancy">
                {({ field, error }) => (
                  <UzNumberInput
                    name={field.name}
                    label="Base occupancy (adults)"
                    precision={0}
                    min={1}
                    max={40}
                    value={(field.value as number | null | undefined) ?? ''}
                    onValueChange={(v) => field.onChange(v ?? null)}
                    hint="Adults the base rate covers: 2 = double, 3 = triple, or the whole-unit capacity for a villa/houseboat (e.g. 6). Blank = 2. Guests beyond this are charged the extra-adult rate; beyond the room's max occupancy they're flagged unavailable."
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
            </div>
          </Group>

          <Group title="Price components (per night)">
            <div className="md:w-1/2 md:pr-2">
              <UzFormField name="currencyId">
                {({ field, error }) => (
                  <CurrencyPicker
                    value={(field.value as string | null | undefined) ?? null}
                    onValueChange={(v) => field.onChange(v)}
                    disabled={pending}
                    error={error}
                  />
                )}
              </UzFormField>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <MoneyField
                name="adultPrice"
                label="Per adult"
                required
                pending={pending}
                symbol={symbol}
              />
              <MoneyField
                name="childWithBedPrice"
                label="Per child"
                required
                pending={pending}
                symbol={symbol}
              />
              <MoneyField name="infantPrice" label="Infant" pending={pending} symbol={symbol} />
              <MoneyField
                name="extraAdultPrice"
                label="Extra adult"
                hint="Adult beyond standard occupancy (extra bed included)"
                pending={pending}
                symbol={symbol}
              />
              <MoneyField
                name="extraChildPrice"
                label="Extra child"
                hint="Child beyond standard occupancy, sharing bedding"
                pending={pending}
                symbol={symbol}
              />
              <MoneyField
                name="extraBedChildPrice"
                label="Extra bed (child)"
                hint="Child on a rollaway/cot, per night"
                pending={pending}
                symbol={symbol}
              />
            </div>
            <MoneyField
              name="fixedRoomCost"
              label="Fixed room cost"
              hint="Whole-room price for buyouts. Leave empty when pricing per person."
              pending={pending}
              symbol={symbol}
            />
            <PricePreview values={watched} symbol={symbol} />
          </Group>

          <Group title="Meals">
            <p className="text-xs text-zinc-500">
              Per person per night, added on top of the room price for this period. "None" is always
              available at {symbol}0.
            </p>
            {mealOptions.length === 0 && (
              <p className="text-xs text-zinc-400">No meals configured - sells as None.</p>
            )}
            <div className="space-y-2">
              {mealOptions.map((opt, i) => (
                <div key={i} className="grid grid-cols-[1fr_110px_110px_32px] items-end gap-2">
                  <MealPlanPicker
                    label={i === 0 ? 'Meal plan' : ''}
                    value={opt.mealPlanId}
                    onValueChange={(v) =>
                      setMealOptions(
                        mealOptions.map((m, j) => (j === i ? { ...m, mealPlanId: v } : m)),
                      )
                    }
                    disabled={pending}
                  />
                  <UzNumberInput
                    label={i === 0 ? 'Adult' : undefined}
                    precision={2}
                    min={0}
                    leftAdornment={<span className="text-sm text-zinc-500">{symbol}</span>}
                    value={opt.perAdult || ''}
                    onValueChange={(v) =>
                      setMealOptions(
                        mealOptions.map((m, j) => (j === i ? { ...m, perAdult: v ?? 0 } : m)),
                      )
                    }
                    disabled={pending}
                  />
                  <UzNumberInput
                    label={i === 0 ? 'Child' : undefined}
                    precision={2}
                    min={0}
                    leftAdornment={<span className="text-sm text-zinc-500">{symbol}</span>}
                    value={opt.perChild || ''}
                    onValueChange={(v) =>
                      setMealOptions(
                        mealOptions.map((m, j) => (j === i ? { ...m, perChild: v ?? 0 } : m)),
                      )
                    }
                    disabled={pending}
                  />
                  <UzButton
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setMealOptions(mealOptions.filter((_, j) => j !== i))}
                    disabled={pending}
                    aria-label="Remove meal option"
                    className="h-9 w-8 text-zinc-400 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </UzButton>
                </div>
              ))}
            </div>
            <UzButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setMealOptions([...mealOptions, { mealPlanId: null, perAdult: 0, perChild: 0 }])
              }
              disabled={pending}
              className="h-7 text-xs"
            >
              <Plus className="mr-1 h-3 w-3" />
              Add meal
            </UzButton>
          </Group>

          <Group title="Availability">
            <UzFormField name="isActive">
              {({ field }) => (
                <UzSwitch
                  label="Active"
                  description="Inactive periods are hidden from pickers."
                  checked={field.value !== false}
                  onCheckedChange={field.onChange}
                  disabled={pending}
                />
              )}
            </UzFormField>

            {/* Blackout dates - sold-out / closed days inside the window. */}
            <div className="space-y-2">
              <span className="text-xs font-medium text-zinc-700">Blackout dates</span>
              <p className="text-xs text-zinc-500">
                Days inside this period that are sold out or closed. They show as blocked on the
                price calendar and are skipped by the itinerary picker.
              </p>
              <div className="md:w-1/2">
                <UzDateTimePicker
                  mode="date"
                  displayFormat="dd/MM/yyyy"
                  placeholder="Add a blackout date (dd/mm/yyyy)"
                  minDate={ymdToDate(watched.validityFrom) ?? undefined}
                  maxDate={ymdToDate(watched.validityTo) ?? undefined}
                  value={null}
                  onValueChange={(d) => {
                    if (d) addBlackout(dateToYmd(d));
                  }}
                  disabled={pending}
                />
              </div>
              {blackoutDates.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {blackoutDates.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700"
                    >
                      {d}
                      <button
                        type="button"
                        onClick={() => removeBlackout(d)}
                        disabled={pending}
                        aria-label={`Remove ${d}`}
                        className="text-zinc-400 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Group>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50/40 px-5 py-3">
          <UzButton type="button" variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton type="submit" size="sm" loading={pending}>
            {initial ? 'Save period' : 'Add period'}
          </UzButton>
        </footer>
      </UzForm>
    </div>
  );
};
